// ===================================================================
// Impact — Map View
// Interactive map with sidebar filters for damage assessments
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Checkbox, SegmentedControl,
  LoadingOverlay,
} from '@mantine/core';
import { IconFilter } from '@tabler/icons-react';
import { useImpactStore } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import AssessmentMap from './components/AssessmentMap';

// ── Constants ──────────────────────────────────────────────

const ASSESSMENT_TYPES = [
  { value: 'individual', label: 'Individual (IA)' },
  { value: 'public', label: 'Public (PA)' },
];

const DAMAGE_CATEGORIES = [
  { value: 'affected', label: 'Affected', color: '#16a34a' },
  { value: 'minor', label: 'Minor', color: '#d97706' },
  { value: 'major', label: 'Major', color: '#ea580c' },
  { value: 'destroyed', label: 'Destroyed', color: '#dc2626' },
  { value: 'inaccessible', label: 'Inaccessible', color: '#7c3aed' },
];

const SEVERITY_LEVELS = [
  { value: 1, label: 'Severity 1', color: '#16a34a' },
  { value: 2, label: 'Severity 2', color: '#22c55e' },
  { value: 3, label: 'Severity 3', color: '#d97706' },
  { value: 4, label: 'Severity 4', color: '#ea580c' },
  { value: 5, label: 'Severity 5', color: '#dc2626' },
];

// ── Component ──────────────────────────────────────────────

export default function MapView() {
  const { assessments, isLoading, loadAssessments } = useImpactStore();
  const { activeIncident } = useAuthStore();

  const [colorBy, setColorBy] = useState<'category' | 'severity'>('category');
  const [typeFilters, setTypeFilters] = useState<string[]>(ASSESSMENT_TYPES.map(t => t.value));
  const [categoryFilters, setCategoryFilters] = useState<string[]>(DAMAGE_CATEGORIES.map(c => c.value));
  const [severityFilters, setSeverityFilters] = useState<number[]>(SEVERITY_LEVELS.map(s => s.value));

  useEffect(() => { loadAssessments(); }, [loadAssessments]);

  // Filter assessments by incident + map filters
  const filtered = useMemo(() => {
    return assessments.filter(a => {
      if (!a.latitude || !a.longitude) return false;
      if (activeIncident && String(a.incidentId) !== String(activeIncident.id)) return false;
      if (!typeFilters.includes(a.assessmentType)) return false;
      if (a.damageCategory && !categoryFilters.includes(a.damageCategory)) return false;
      if (a.severity && !severityFilters.includes(a.severity)) return false;
      return true;
    });
  }, [assessments, activeIncident, typeFilters, categoryFilters, severityFilters]);

  const incidentAssessments = useMemo(() => {
    if (!activeIncident) return assessments;
    return assessments.filter(a => String(a.incidentId) === String(activeIncident.id));
  }, [assessments, activeIncident]);
  const geoCount = incidentAssessments.filter(a => a.latitude && a.longitude).length;
  const noGeoCount = incidentAssessments.length - geoCount;

  const toggleType = (val: string) => {
    setTypeFilters(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    );
  };

  const toggleCategory = (val: string) => {
    setCategoryFilters(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    );
  };

  const toggleSeverity = (val: number) => {
    setSeverityFilters(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    );
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Assessment Map</Title>
          {activeIncident && (
            <Badge size="sm" variant="light" color="blue">{activeIncident.name}</Badge>
          )}
        </Group>
        <Group gap="xs">
          <Badge size="sm" variant="outline" color="green">{geoCount} mapped</Badge>
          {noGeoCount > 0 && (
            <Badge size="sm" variant="outline" color="gray">{noGeoCount} no location</Badge>
          )}
        </Group>
      </Group>

      <div style={{ display: 'flex', gap: 16 }}>
        {/* Sidebar Filters */}
        <Paper p="md" radius="sm" withBorder style={{ width: 240, flexShrink: 0 }}>
          <Group gap="xs" mb="md">
            <IconFilter size={14} />
            <Text fw={600} size="sm">Filters</Text>
          </Group>

          {/* Color By Toggle */}
          <Text size="xs" fw={500} c="dimmed" mb={4}>Color By</Text>
          <SegmentedControl
            value={colorBy}
            onChange={(v) => setColorBy(v as 'category' | 'severity')}
            data={[
              { value: 'category', label: 'Category' },
              { value: 'severity', label: 'Severity' },
            ]}
            size="xs"
            fullWidth
            mb="md"
          />

          {/* Assessment Type Filters */}
          <Text size="xs" fw={500} c="dimmed" mb={4}>Assessment Type</Text>
          <Stack gap={4} mb="md">
            {ASSESSMENT_TYPES.map(t => (
              <Checkbox
                key={t.value}
                label={t.label}
                checked={typeFilters.includes(t.value)}
                onChange={() => toggleType(t.value)}
                size="xs"
              />
            ))}
          </Stack>

          {/* Category Filters */}
          <Text size="xs" fw={500} c="dimmed" mb={4}>Damage Category</Text>
          <Stack gap={4} mb="md">
            {DAMAGE_CATEGORIES.map(c => (
              <Checkbox
                key={c.value}
                label={
                  <Group gap={6}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: c.color,
                    }} />
                    <Text size="xs">{c.label}</Text>
                  </Group>
                }
                checked={categoryFilters.includes(c.value)}
                onChange={() => toggleCategory(c.value)}
                size="xs"
              />
            ))}
          </Stack>

          {/* Severity Filters */}
          <Text size="xs" fw={500} c="dimmed" mb={4}>Severity Level</Text>
          <Stack gap={4}>
            {SEVERITY_LEVELS.map(s => (
              <Checkbox
                key={s.value}
                label={
                  <Group gap={6}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: s.color,
                    }} />
                    <Text size="xs">{s.label}</Text>
                  </Group>
                }
                checked={severityFilters.includes(s.value)}
                onChange={() => toggleSeverity(s.value)}
                size="xs"
              />
            ))}
          </Stack>

          {/* Legend */}
          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--frost)' }}>
            <Text size="xs" fw={500} c="dimmed" mb={4}>
              {colorBy === 'category' ? 'Category Legend' : 'Severity Legend'}
            </Text>
            <Stack gap={2}>
              {colorBy === 'category'
                ? DAMAGE_CATEGORIES.map(c => (
                    <Group key={c.value} gap={6}>
                      <div style={{
                        width: 12, height: 12, borderRadius: '50%',
                        background: c.color, border: '2px solid white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                      <Text size="xs">{c.label}</Text>
                    </Group>
                  ))
                : SEVERITY_LEVELS.map(s => (
                    <Group key={s.value} gap={6}>
                      <div style={{
                        width: 12, height: 12, borderRadius: '50%',
                        background: s.color, border: '2px solid white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                      <Text size="xs">{s.label}</Text>
                    </Group>
                  ))
              }
            </Stack>
          </div>

          {/* Filter Stats */}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--frost)' }}>
            <Text size="xs" c="dimmed">
              Showing {filtered.length} of {geoCount} mapped assessments
            </Text>
          </div>
        </Paper>

        {/* Map */}
        <div style={{ flex: 1, position: 'relative', minHeight: 500 }}>
          <LoadingOverlay visible={isLoading} />
          <AssessmentMap
            assessments={filtered}
            height="100%"
            colorBy={colorBy}
          />
        </div>
      </div>
    </div>
  );
}
